import axios from './axiosConfig';
import { environment } from '../config/environment';
import { IPushDailyStat, IPushDeviceStats, IPushFilters, IPushStatsFilterData } from '../types/pushStats';

export interface IPushDailyStatsParams extends IPushFilters {
  startDate: string;
  endDate: string;
}

// A server that doesn't know a route can answer 200 with its index.html (SPA
// fallback), which axios hands over as a string. Fail with a clear message
// instead of letting a string flow into the page.
const NOT_AVAILABLE =
  'Push stats are not available on this server (unexpected response). The backend may need to be updated.';

const expectArray = <T>(data: unknown): T[] => {
  if (!Array.isArray(data)) throw NOT_AVAILABLE;
  return data as T[];
};

const expectObject = <T>(data: unknown): T => {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) throw NOT_AVAILABLE;
  return data as T;
};

export const pushStatsService = {
  getDailyStats: async (params: IPushDailyStatsParams): Promise<IPushDailyStat[]> => {
    const response = await axios.get<unknown>(`${environment.apiUrl}push/stats/daily`, {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        source: params.source,
        apiKeyId: params.apiKeyId || undefined,
        notificationType: params.notificationType || undefined,
        couponId: params.couponId || undefined,
      },
    });
    return expectArray<IPushDailyStat>(response.data);
  },

  getDeviceStats: async (): Promise<IPushDeviceStats> => {
    const response = await axios.get<unknown>(`${environment.apiUrl}push/stats/devices`);
    const stats = expectObject<IPushDeviceStats>(response.data);
    if (!Array.isArray(stats.distribution)) throw NOT_AVAILABLE;
    return stats;
  },

  getFilterData: async (): Promise<IPushStatsFilterData> => {
    const response = await axios.get<unknown>(`${environment.apiUrl}push/stats/filterdata`);
    const data = expectObject<IPushStatsFilterData>(response.data);
    if (!Array.isArray(data.apiKeys) || !Array.isArray(data.notificationTypes) || !Array.isArray(data.coupons)) {
      throw NOT_AVAILABLE;
    }
    return data;
  },
};
